'use strict';

import { getLoggedServingsOfRecipe, getParticipantsInfo, getParticipantsForSpecificMeal } from '../utils/Meals';
import { flatMap, uniq, uniqBy } from 'lodash';
import uuidGen from 'uuid';

export function generateOffsets(user, meals, offset, mealType, participants, totalDays) {
    const existingMeal = meals.find(meal => meal.meal === mealType && offset === meal.offset);

    let existingMealLeftovers = [];
    if (existingMeal) {
        existingMealLeftovers = meals.filter((m) => m.parent == existingMeal.id);
    }

    const totalLeftovers = totalDays ? totalDays : existingMealLeftovers.length;

    const leftovers = [];

    let currentOffset = offset;

    for (let i = 0; i < totalLeftovers && leftovers.length < 5; i++) {
        const leftoverOffset = existingMealLeftovers[i] ? existingMealLeftovers[i].offset : currentOffset + 1;
        const leftoverMealType = existingMealLeftovers[i] ? existingMealLeftovers[i].meal : mealType;
        const leftoverParticipants = existingMealLeftovers[i] ? existingMealLeftovers[i].participants : participants;
        const participantDetails  = getParticipantsInfo(user);
        const leftoverParticipantsInfo = participantDetails.filter(p => leftoverParticipants.includes(p.uuid));
        leftovers.push({leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipantsInfo});
    }

    return leftovers;
}

export function getParticipantsForMeal(plan, meal, profile) {

    if (!profile) {
        return null;
    }

    if (!meal.participants) {
        const name = profile.name ? profile.name : profile.first_name;
        //Use portion of 1 because when a meal doesn't have participants it is a food that should not be pre-scaled
        return [{ uuid: 'USER', name, portion: 1 }];
    }

    let guestNo = 1;
    const mealAndLeftovers = getMealAndLeftovers(meal, plan);

    const participantDetails = getParticipantsInfo(profile);
    const participants = uniq(flatMap(mealAndLeftovers, m => m.participants));

    return participants?.map((uuid) => {
        const member = participantDetails.find(p => p.uuid === uuid);

        if (member) {
            return member;
        }

        if (!uuid || uuid === '' || uuid.startsWith('guest-')) {
            return { uuid, name: 'Guest #' + guestNo++, portion: 1 };
        }

        return null;
    }).filter(p => p !== null);
}

export function getMealAndLeftovers(meal, plan) {
    let parent = null;
    let allMeals = [];

    if (meal.meal_type == 'leftover') {
        parent = plan.items.find((item) => item.id === meal.parent);
    } else {
        parent = meal;
    }

    allMeals = plan.items.filter((item) => item.parent === parent.id && !item.deleted);
    allMeals.unshift(parent);

    return allMeals;
}

// Alias for EditMealServingsModalPrimary universal compatibility
export function getMealAndLeftoversFromPlan(meal, plan) {
    return getMealAndLeftovers(meal, plan);
}

/**
 * Retrieves batch information for a given meal, including participants, servings needed and total days.
 *
 * @param {number} offset - The day offset for the meal in the plan.
 * @param {string} mealType - The type of meal to retrieve.
 * @param {Object} profile - The user profile, used to get participants for the meal.
 * @param {Object} plan - The meal plan object which contains all scheduled meals and related information.
 *
 * @returns {Object|null} An object, or null if no meal is found.
 * @returns {Array} return.participants - An array of participants for the meal.
 * @returns {number} return.mealServingsNeeded - Total number of servings needed for the meal.
 * @returns {number} return.totalDays - The total number of days covered by the meal and leftovers.
 */
export function getBatchInfo(offset, mealType, profile, plan) {
    const alreadyScheduledMeal = plan.items.find(
        (m) => m.meal === mealType && m.offset === offset && m.meal_type !== 'leftover' && !m.deleted
    );

    if (alreadyScheduledMeal) {
        const allMeals = getMealAndLeftovers(alreadyScheduledMeal, plan);
        const totalDays = allMeals.length;

        // Sum servings needed across ALL meals (main + leftovers) individually
        // Each meal can have different participants, so we cannot multiply by totalDays
        let mealServingsNeeded = 0;
        allMeals.forEach(meal => {
            const mealParticipants = getParticipantsForSpecificMeal(meal, profile);
            mealServingsNeeded += mealParticipants.reduce((sum, p) => sum + p.portion, 0);
        });

        // Get participants for display (use main meal participants)
        const participants = getParticipantsForMeal(plan, alreadyScheduledMeal, profile);

        return {
            participants,
            mealServingsNeeded,
            totalDays,
        };
    }

    return null;
}

/**
 * Reuses existing leftovers when possible, creates new ones when needed (PRO version)
 *
 * Similar to updateMealLeftovers but for PRO's offset-based system.
 * Implements consumer pattern:
 * 1. Finds existing leftovers for each dish
 * 2. Tries to reuse existing leftovers (preferring exact location match)
 * 3. Only creates new leftovers if no existing ones available
 * 4. Tracks which leftovers were reused
 *
 * @param {Array} dishes - Dishes to create leftovers for (meal_type !== 'leftover')
 * @param {Array} leftoverOffsets - Selected leftover offsets [{leftoverOffset, mealType, participants}]
 * @param {Array} allMeals - All meals in the plan
 * @returns {{leftoverMeals: Array, reusedLeftoverIds: Set}} New/reused leftovers and set of reused IDs
 */
export function reuseOrCreateLeftovers(dishes, leftoverOffsets, allMeals) {
    const leftoverMeals = [];
    const reusedLeftoverIds = new Set();

    dishes.forEach(dish => {
        if (dish.meal_type !== 'leftover') {
            // Find ALL existing leftovers for this dish
            const existingLeftovers = allMeals.filter(meal =>
                meal.meal_type === 'leftover' &&
                meal.parent === dish.id &&
                !meal.deleted
            );

            // Create a pool of existing leftovers we can reuse
            const availableLeftovers = [...existingLeftovers];

            // For each selected offset, either REUSE an existing leftover or CREATE new
            leftoverOffsets.forEach(({ leftoverOffset, mealType, participants: leftoverParticipants }) => {
                // Try to find existing leftover at this exact location first
                let leftoverIndex = availableLeftovers.findIndex(lo =>
                    lo.offset === leftoverOffset && lo.meal === mealType
                );

                // If not found at exact location, use any available leftover
                if (leftoverIndex === -1) {
                    leftoverIndex = availableLeftovers.findIndex(lo => true);
                }

                let leftover;
                if (leftoverIndex !== -1) {
                    // REUSE existing leftover (preserves ID)
                    const existingLeftover = availableLeftovers.splice(leftoverIndex, 1)[0];
                    leftover = {
                        ...existingLeftover,
                        offset: leftoverOffset,
                        meal: mealType,
                        participants: leftoverParticipants.map(p => p.uuid),
                        scaling: dish.scaling,
                        deleted: false
                    };
                    reusedLeftoverIds.add(existingLeftover.id);
                } else {
                    // CREATE new leftover only if no existing one available
                    leftover = {
                        ...dish,
                        id: uuidGen.v4().substring(0, 8),
                        meal_type: 'leftover',
                        offset: leftoverOffset,
                        meal: mealType,
                        parent: dish.id,
                        participants: leftoverParticipants.map(p => p.uuid),
                        scaling: dish.scaling
                    };
                }

                leftoverMeals.push(leftover);
            });
        }
    });

    return { leftoverMeals, reusedLeftoverIds };
}

/**
 * Finds existing leftovers that weren't reused and marks them for removal
 *
 * Completes the consumer pattern by cleaning up unreused leftovers.
 *
 * @param {Array} dishes - Dishes whose leftovers to check
 * @param {Set} reusedLeftoverIds - IDs of leftovers that were reused
 * @param {Array} allMeals - All meals in the plan
 * @returns {Array} mealsToRemove - Leftovers to remove
 */
export function removeUnusedLeftovers(dishes, reusedLeftoverIds, allMeals) {
    const mealsToRemove = [];

    dishes.forEach(dish => {
        if (dish.meal_type !== 'leftover') {
            const existingLeftovers = allMeals.filter(meal =>
                meal.meal_type === 'leftover' &&
                meal.parent === dish.id &&
                !meal.deleted
            );

            existingLeftovers.forEach(leftover => {
                // If this leftover wasn't reused, mark it for removal
                if (!reusedLeftoverIds.has(leftover.id) && !mealsToRemove.some(m => m.id === leftover.id)) {
                    mealsToRemove.push(leftover);
                }
            });
        }
    });

    return mealsToRemove;
}

/**
 * Finds the primary (parent) meal for a given meal
 *
 * If the meal is a leftover, returns its parent meal.
 * Otherwise returns the meal itself.
 *
 * @param {Object} meal - The meal (could be leftover or primary)
 * @param {Array} allMeals - All meals in the plan
 * @returns {Object} The primary meal
 */
export function findPrimaryMeal(meal, allMeals) {
    if (meal.meal_type === 'leftover') {
        return allMeals.find(m => (m.id === meal.parent || m.uuid === meal.parent));
    }
    return meal;
}

/**
 * Gets other meals in the family (excluding provided meals) that need scaling update
 *
 * This function:
 * 1. Finds the primary meal (if editing a leftover)
 * 2. Gets all meals in the family using getMealAndLeftovers
 * 3. Filters out meals already in updatedMeals, leftoverMeals, or mealsToRemove
 * 4. Returns remaining family meals with updated scaling
 *
 * @param {Object} targetMeal - The meal being edited
 * @param {Array} updatedMeals - Meals already being updated (e.g., slot meals)
 * @param {Array} leftoverMeals - New/updated leftover meals
 * @param {Array} mealsToRemove - Meals being removed
 * @param {Array} allMeals - All meals in plan
 * @param {number} familyScaling - New scaling to apply
 * @returns {Array} otherFamilyMeals - Other family meals with updated scaling
 */
export function getOtherFamilyMeals(targetMeal, updatedMeals, leftoverMeals, mealsToRemove, allMeals, familyScaling) {
    const otherFamilyMeals = [];

    // Find primary meal if editing leftover
    const primaryMeal = findPrimaryMeal(targetMeal, allMeals);
    if (!primaryMeal) {
        return otherFamilyMeals;
    }

    // Get all meals in family
    const mealFamily = getMealAndLeftovers(primaryMeal, { items: allMeals });

    mealFamily.forEach(familyMeal => {
        // Helper to check if meal is in array
        const isInArray = (meal, array) => {
            return array.some(m =>
                (m.id && meal.id && m.id === meal.id) ||
                (m.uuid && meal.uuid && m.uuid === meal.uuid)
            );
        };

        // Skip if already in updatedMeals, leftoverMeals, or mealsToRemove
        const alreadyIncluded =
            isInArray(familyMeal, updatedMeals) ||
            isInArray(familyMeal, leftoverMeals) ||
            isInArray(familyMeal, mealsToRemove);

        // Only include meals with recipes/foods that aren't already being updated
        if (!alreadyIncluded && (familyMeal.recipe_uuid || familyMeal.food_uuid)) {
            otherFamilyMeals.push({
                ...familyMeal,
                scaling: familyScaling
            });
        }
    });

    return otherFamilyMeals;
}